"use client";

import { errorLoggingService } from "@/lib/error-logging-service";
import { db, getClientFunctions, storage } from "@/lib/firebase-client";
import { canEditRecipe, getUserRoleInBox } from "@/lib/recipe-box-permissions";
import type { FirestoreTimestamp, Recipe, RecipeBox } from "@/lib/types";
import { uploadImage } from "@ezrecipebox/shared";
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    type FirestoreError,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

const RECIPES_COLLECTION = "recipes";

type CreateRecipeCallableInput = {
  boxId: string;
  title: string;
  labels?: string[];
  ingredients?: string[];
  directions?: string;
  notes?: string;
  prepTime?: string;
  cookTime?: string;
  servings?: string;
  imageHint?: string;
  isFavorite?: boolean;
  collectionIds?: string[];
  isDraft?: boolean;
};

type CreateRecipeCallableResult = { recipeId: string };
type UpdateRecipeCallableInput = { recipeId: string; patch: Record<string, unknown> };
type DeleteRecipeCallableInput = { recipeId: string };
type GetRecipeImageUploadPathInput = { recipeId: string; fileName: string; contentType?: string };
type GetRecipeImageUploadPathResult = { storagePath: string };
type FinalizeRecipeImageInput = { recipeId: string; storagePath: string; imageUrl?: string };
type RemoveRecipeImageInput = { recipeId: string };
type SetRecipeSharingInput = {
  recipeId: string;
  isPublic: boolean;
  password?: string | null;
  regenerateToken?: boolean;
};
type CloneRecipeCallableInput = { sourceRecipeId: string; newRecipeBoxId?: string; titlePrefix?: string };
type CloneRecipeCallableResult = { recipeId: string };

async function callFunctions<TInput, TOutput>(name: string, input: TInput): Promise<TOutput> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }
  const callable = httpsCallable<TInput, TOutput>(functions, name);
  const response = await callable(input);
  return response.data;
}

export type CreateRecipeInput = {
  recipeBoxId: string;
  title: string;
  labels?: string[];
  ingredients: string[];
  directions: string;
  notes?: string;
  prepTime?: string;
  cookTime?: string;
  servings?: string;
  imageFile?: File | null;
  imageHint?: string;
  sourceImageUrl?: string;
  isPublic?: boolean;
  sharePassword?: string; // Plain text password (will be hashed)
  isFavorite?: boolean;
  collectionIds?: string[];
  isDraft?: boolean; // Draft recipe visible only to owner
};

export type UpdateRecipeInput = Partial<Omit<CreateRecipeInput, "recipeBoxId">> & {
  imageStoragePath?: string | null;
  removeImage?: boolean;
  isPublic?: boolean;
  sharePassword?: string; // Plain text password (will be hashed), or undefined to remove
  regenerateShareToken?: boolean; // Regenerate share token
  isDraft?: boolean; // Toggle draft status
  imageFocalPointX?: number;
  imageFocalPointY?: number;
  imageZoom?: number;
};

type RecipeFirestoreDocument = {
  ownerUid: string;
  recipeBoxId: string;
  title: string;
  labels: string[];
  ingredients: string[];
  directions: string;
  notes: string;
  imageUrl: string;
  imageHint: string;
  imageStoragePath?: string;
  imageFocalPointX?: number;
  imageFocalPointY?: number;
  imageZoom?: number;
  prepTime?: string;
  cookTime?: string;
  servings?: string;
  isPublic?: boolean;
  shareToken?: string; // Unique token for shareable URLs
  sharePassword?: string; // Hashed password for protected shares
  isFavorite?: boolean;
  collectionIds?: string[];
  isDraft?: boolean; // Draft recipe visible only to owner
  createdAt?: FirestoreTimestamp;
  updatedAt?: FirestoreTimestamp;
};

/**
 * IMPORTANT:
 * Do NOT create Firestore refs at module scope.
 * Next.js can evaluate client modules during prerender/SSR, and `db` may not be initialized then.
 */
function getRecipesCollection() {
  return collection(db, RECIPES_COLLECTION);
}

const sanitizeArray = (values: readonly string[] | undefined): string[] =>
  values?.map((value) => value.trim()).filter(Boolean) ?? [];

const sanitizeText = (value: string | undefined | null): string => value?.trim() ?? "";

const sanitizeOptionalText = (value: string | undefined | null): string | undefined => {
  const sanitized = sanitizeText(value);
  return sanitized.length > 0 ? sanitized : undefined;
};

/**
 * Compress image for faster uploads (Web SPA only)
 */
async function compressImage(file: File): Promise<Blob> {
  try {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to compress image'));
          }, 'image/jpeg', 0.85);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  } catch (error) {
    await errorLoggingService.logError({
      context: 'compressImage',
      error: error as Error,
      additionalData: {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }
    });
    throw error;
  }
}

/**
 * Helper to upload a recipe image using the shared upload orchestration.
 * Handles: get path → upload to storage → finalize with server.
 */
async function uploadRecipeImage(recipeId: string, file: File): Promise<void> {
  try {
    await uploadImage({
      file,
      getFileName: (f) => f.name,
      getContentType: (f) => f.type,
      getUploadPath: async (fileName, contentType) => {
        const result = await callFunctions<GetRecipeImageUploadPathInput, GetRecipeImageUploadPathResult>(
          "getRecipeImageUploadPath",
          { recipeId, fileName, contentType },
        );
        return result.storagePath;
      },
      uploadToStorage: async (f, storagePath, contentType) => {
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, f, { contentType });
        return getDownloadURL(storageRef);
      },
      finalizeUpload: async (storagePath, imageUrl) => {
        await callFunctions<FinalizeRecipeImageInput, { success?: boolean }>("finalizeRecipeImage", {
          recipeId,
          storagePath,
          imageUrl,
        });
      },
    });
  } catch (error) {
    await errorLoggingService.logError({
      context: 'uploadRecipeImage',
      error: error as Error,
      additionalData: {
        recipeId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }
    });
    throw error;
  }
}

export async function createRecipe(
  _ownerUid: string,
  input: CreateRecipeInput,
  onProgress?: (status: string, progress: number) => void,
): Promise<string> {
  onProgress?.("Creating recipe...", 0);

  // Prepare image for upload if provided
  let compressedFile: File | null = null;
  if (input.imageFile) {
    onProgress?.("Compressing image...", 10);
    try {
      const compressedBlob = await compressImage(input.imageFile);
      // Convert Blob back to File with original filename
      compressedFile = new File([compressedBlob], input.imageFile.name, {
        type: compressedBlob.type || input.imageFile.type,
        lastModified: Date.now(),
      });
      onProgress?.("Image compressed", 20);
    } catch (error) {
      await errorLoggingService.logWarning({
        context: 'createRecipe - image compression fallback',
        message: 'Image compression failed, using original file',
        additionalData: {
          fileName: input.imageFile.name,
          fileSize: input.imageFile.size,
          error: (error as Error).message,
        }
      });
      compressedFile = input.imageFile;
      onProgress?.("Using original image", 20);
    }
  }

  onProgress?.("Saving recipe details...", 30);

  // Phase 7: Create via callable (server-owned write) with sharing settings included
  const result = await callFunctions<CreateRecipeCallableInput & {
    isPublic?: boolean;
    sharePassword?: string;
    isDraft?: boolean;
  }, CreateRecipeCallableResult>("createRecipe", {
    boxId: input.recipeBoxId,
    title: sanitizeText(input.title),
    labels: sanitizeArray(input.labels),
    ingredients: sanitizeArray(input.ingredients),
    directions: sanitizeText(input.directions),
    notes: sanitizeOptionalText(input.notes),
    prepTime: sanitizeOptionalText(input.prepTime),
    cookTime: sanitizeOptionalText(input.cookTime),
    servings: sanitizeOptionalText(input.servings),
    imageHint: sanitizeOptionalText(input.imageHint),
    isFavorite: input.isFavorite,
    collectionIds: input.collectionIds,
    // Include sharing settings in initial creation to reduce round trips
    isPublic: input.isPublic,
    sharePassword: input.sharePassword,
    isDraft: input.isDraft,
  });

  const recipeId = result.recipeId;
  onProgress?.("Recipe created", 50);

  // Image flow: upload compressed image if provided
  if (compressedFile) {
    onProgress?.("Uploading image...", 60);
    try {
      await uploadRecipeImage(recipeId, compressedFile);
      onProgress?.("Image uploaded", 90);
    } catch (error) {
      await errorLoggingService.logError({
        context: 'createRecipe - image upload failed',
        error: error as Error,
        additionalData: {
          recipeId,
          fileName: compressedFile.name,
          fileSize: compressedFile.size,
        }
      });
      throw error;
    }
  } else if (input.sourceImageUrl) {
    // Allow external URL for imported/scraped recipes
    onProgress?.("Setting image URL...", 60);
    await callFunctions<UpdateRecipeCallableInput, { success?: boolean }>("updateRecipe", {
      recipeId,
      patch: { imageUrl: input.sourceImageUrl, imageHint: sanitizeOptionalText(input.imageHint) ?? undefined },
    });
    onProgress?.("Image URL set", 90);
  }

  // NOTE: Sharing settings are now handled in initial createRecipe call above
  // No need for separate setRecipeSharing call anymore

  onProgress?.("Complete!", 100);
  return recipeId;
}

export async function deleteRecipe(
  _recipeBoxId: string,  
  recipeId: string,
  _imageStoragePath?: string, // eslint-disable-line @typescript-eslint/no-unused-vars
  _recipeBox?: RecipeBox | null, // eslint-disable-line @typescript-eslint/no-unused-vars
  _userId?: string | null, // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<void> {
  // Phase 7: Delete via callable. Permissions are enforced server-side.
  // Keep arguments for backward compatibility with existing call sites.
  // Parameters are kept for backward compatibility; server enforces permissions.
  await callFunctions<DeleteRecipeCallableInput, { success?: boolean }>("deleteRecipe", { recipeId });
}

export async function updateRecipe(
  ownerUid: string,
  recipeId: string,
  input: UpdateRecipeInput,
  recipeBox?: RecipeBox | null,
): Promise<void> {
  const recipeRef = doc(getRecipesCollection(), recipeId);
  const snapshot = await getDoc(recipeRef);

  if (!snapshot.exists()) {
    throw new Error("Recipe not found.");
  }

  const existing = snapshot.data() as RecipeFirestoreDocument;
  const recipe: Recipe = {
    id: recipeId,
    recipeBoxId: existing.recipeBoxId,
    ownerUid: existing.ownerUid,
    title: existing.title,
    labels: existing.labels ?? [],
    ingredients: existing.ingredients ?? [],
    directions: existing.directions ?? "",
    notes: existing.notes,
    imageUrl: existing.imageUrl || "",
    imageHint: existing.imageHint || "recipe",
    imageStoragePath: existing.imageStoragePath,
    prepTime: existing.prepTime,
    cookTime: existing.cookTime,
    servings: existing.servings,
    createdAt: existing.createdAt,
    updatedAt: existing.updatedAt,
  };

  // Check permissions if recipe box is provided
  if (recipeBox && !canEditRecipe(recipeBox, recipe, ownerUid)) {
    throw new Error("You do not have permission to edit this recipe.");
  }

  // Fallback check if recipe box is not provided (for backward compatibility)
  if (!recipeBox && existing.ownerUid !== ownerUid) {
    throw new Error("You do not have permission to edit this recipe.");
  }

  // Phase 7: Update core fields via callable (server-owned write).
  const patch: Record<string, unknown> = {};
  if (typeof input.title === "string") patch.title = sanitizeText(input.title);
  if (Array.isArray(input.labels)) patch.labels = sanitizeArray(input.labels);
  if (Array.isArray(input.ingredients)) patch.ingredients = sanitizeArray(input.ingredients);
  if (typeof input.directions === "string") patch.directions = sanitizeText(input.directions);
  if (input.notes !== undefined) patch.notes = sanitizeText(input.notes);
  if (typeof input.imageHint === "string") patch.imageHint = sanitizeText(input.imageHint) || existing.imageHint;
  if (input.prepTime !== undefined) patch.prepTime = sanitizeOptionalText(input.prepTime) ?? null;
  if (input.cookTime !== undefined) patch.cookTime = sanitizeOptionalText(input.cookTime) ?? null;
  if (input.servings !== undefined) patch.servings = sanitizeOptionalText(input.servings) ?? null;
  if (input.isFavorite !== undefined) patch.isFavorite = input.isFavorite;
  if (input.collectionIds !== undefined) patch.collectionIds = input.collectionIds ?? [];
  if (input.isDraft !== undefined) patch.isDraft = input.isDraft;
  if (input.imageFocalPointX !== undefined) patch.imageFocalPointX = input.imageFocalPointX;
  if (input.imageFocalPointY !== undefined) patch.imageFocalPointY = input.imageFocalPointY;
  if (input.imageZoom !== undefined) patch.imageZoom = input.imageZoom;

  if (Object.keys(patch).length > 0) {
    await callFunctions<UpdateRecipeCallableInput, { success?: boolean }>("updateRecipe", { recipeId, patch });
  }

  // Image flow: upload + finalize; and server-owned removal
  if (input.removeImage) {
    await callFunctions<RemoveRecipeImageInput, { success?: boolean }>("removeRecipeImage", { recipeId });
  } else if (input.imageFile) {
    await uploadRecipeImage(recipeId, input.imageFile);
  } else if (input.sourceImageUrl) {
    await callFunctions<UpdateRecipeCallableInput, { success?: boolean }>("updateRecipe", {
      recipeId,
      patch: { imageUrl: sanitizeText(input.sourceImageUrl) },
    });
  }

  // Sharing is now handled server-side (token + password hashing).
  if (input.isPublic !== undefined || input.sharePassword !== undefined || input.regenerateShareToken) {
    const desiredIsPublic = input.isPublic ?? (existing.isPublic ?? false);
    await callFunctions<SetRecipeSharingInput, { success?: boolean; shareToken: string | null }>("setRecipeSharing", {
      recipeId,
      isPublic: desiredIsPublic === true,
      password: input.sharePassword ?? null,
      regenerateToken: input.regenerateShareToken === true,
    });
  }
}

/**
 * Toggle favorite status of a recipe
 * @param recipeId - The recipe ID
 * @param userId - The user ID
 * @param currentFavorite - The current favorite state (optional, will be read if not provided)
 * @param recipeBox - The recipe box (optional, if provided we can do a direct Firestore update)
 */
export async function toggleRecipeFavorite(
  recipeId: string,
  userId: string,
  currentFavorite?: boolean,
  recipeBox?: RecipeBox | null,
): Promise<boolean> {
  // If current favorite state is not provided, read it from Firestore
  let favoriteState: boolean;
  if (currentFavorite === undefined) {
    const recipeRef = doc(getRecipesCollection(), recipeId);
    const snapshot = await getDoc(recipeRef);

    if (!snapshot.exists()) {
      throw new Error("Recipe not found.");
    }

    const data = snapshot.data() as RecipeFirestoreDocument;
    favoriteState = data.isFavorite ?? false;
  } else {
    favoriteState = currentFavorite;
  }

  const newFavorite = !favoriteState;

  // If we have recipe box data, check permissions client-side and do direct Firestore update
  // This is much faster than going through Firebase Functions
  if (recipeBox) {
    const role = getUserRoleInBox(recipeBox, userId);
    // Any member (owner, member, or viewer) can toggle favorite
    if (role !== null) {
      try {
        const recipeRef = doc(getRecipesCollection(), recipeId);
        await updateDoc(recipeRef, {
          isFavorite: newFavorite,
          updatedAt: serverTimestamp(),
        });
        return newFavorite;
      } catch (error) {
        // If direct update fails (e.g., security rules), fall back to Firebase Function
        console.warn("Direct Firestore update failed, falling back to Firebase Function", error);
      }
    }
  }

  // Fallback to Firebase Function if we don't have recipe box data or direct update failed
  await callFunctions<UpdateRecipeCallableInput, { success?: boolean }>("updateRecipe", {
    recipeId,
    patch: { isFavorite: newFavorite },
  });

  return newFavorite;
}

export function subscribeToRecipes(
  recipeBoxId: string,
  onUpdate: (recipes: Recipe[]) => void,
  onError?: (error: FirestoreError) => void,
): () => void {
  const recipesQuery = query(
    getRecipesCollection(),
    where("recipeBoxId", "==", recipeBoxId),
    orderBy("createdAt", "desc"),
  );

  return onSnapshot(
    recipesQuery,
    (snapshot) => {
      const recipes: Recipe[] = snapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data() as RecipeFirestoreDocument;
        return {
          id: docSnapshot.id,
          recipeBoxId: data.recipeBoxId,
          ownerUid: data.ownerUid,
          title: data.title,
          labels: data.labels ?? [],
          ingredients: data.ingredients ?? [],
          directions: data.directions ?? "",
          notes: data.notes || undefined,
          imageUrl: data.imageUrl || "",
          imageHint: data.imageHint || "recipe",
          imageStoragePath: data.imageStoragePath,
          imageFocalPointX: data.imageFocalPointX,
          imageFocalPointY: data.imageFocalPointY,
          imageZoom: data.imageZoom,
          prepTime: data.prepTime,
          cookTime: data.cookTime,
          servings: data.servings,
          isPublic: data.isPublic ?? false,
          shareToken: data.shareToken,
          isFavorite: data.isFavorite ?? false,
          collectionIds: data.collectionIds,
          isDraft: data.isDraft ?? false,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        };
      });

      onUpdate(recipes);
    },
    onError,
  );
}

export function subscribeToRecipe(
  recipeId: string,
  onUpdate: (recipe: Recipe | null) => void,
  onError?: (error: FirestoreError) => void,
): () => void {
  const recipeRef = doc(getRecipesCollection(), recipeId);

  return onSnapshot(
    recipeRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(null);
        return;
      }

      const data = snapshot.data() as RecipeFirestoreDocument;
      const recipe: Recipe = {
        id: snapshot.id,
        recipeBoxId: data.recipeBoxId,
        ownerUid: data.ownerUid,
        title: data.title,
        labels: data.labels ?? [],
        ingredients: data.ingredients ?? [],
        directions: data.directions ?? "",
        notes: data.notes || undefined,
        imageUrl: data.imageUrl || "",
        imageHint: data.imageHint || "recipe",
        imageStoragePath: data.imageStoragePath,
        prepTime: data.prepTime,
        cookTime: data.cookTime,
        servings: data.servings,
        isPublic: data.isPublic ?? false,
        shareToken: data.shareToken,
        isFavorite: data.isFavorite ?? false,
        collectionIds: data.collectionIds,
        isDraft: data.isDraft ?? false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };

      onUpdate(recipe);
    },
    onError,
  );
}

export type CloneRecipeOptions = {
  newRecipeBoxId?: string; // If provided, clone to different box; otherwise clone to same box
  newOwnerUid?: string; // If provided, set new owner; otherwise keep original owner
  titlePrefix?: string; // Prefix for cloned recipe title (default: "Copy of ")
};

/**
 * Clone a recipe to the same or different recipe box
 * @param sourceRecipeId - ID of the recipe to clone
 * @param options - Cloning options
 * @returns The ID of the newly created cloned recipe
 */
export async function cloneRecipe(
  sourceRecipeId: string,
  options: CloneRecipeOptions = {},
): Promise<string> {
  const { newRecipeBoxId, titlePrefix = "Copy of " } = options;
  // newOwnerUid is intentionally ignored: cloned recipes are owned by the caller.
  const result = await callFunctions<CloneRecipeCallableInput, CloneRecipeCallableResult>("cloneRecipe", {
    sourceRecipeId,
    newRecipeBoxId,
    titlePrefix,
  });
  return result.recipeId;
}

/**
 * Clone multiple recipes at once
 * @param sourceRecipeIds - Array of recipe IDs to clone
 * @param targetRecipeBoxId - Destination recipe box ID
 * @param newOwnerUid - New owner UID for cloned recipes
 * @returns Array of newly created recipe IDs
 */
export async function cloneRecipes(
  sourceRecipeIds: string[],
  targetRecipeBoxId: string,
  newOwnerUid: string,
): Promise<string[]> {
  const clonedIds: string[] = [];

  for (const recipeId of sourceRecipeIds) {
    try {
      const clonedId = await cloneRecipe(recipeId, {
        newRecipeBoxId: targetRecipeBoxId,
        newOwnerUid,
        titlePrefix: "Copy of ",
      });
      clonedIds.push(clonedId);
    } catch (error) {
      await errorLoggingService.logError({
        context: 'cloneMultipleRecipes',
        error: error as Error,
        additionalData: { recipeId, targetRecipeBoxId },
      });
      // Continue with other recipes even if one fails
    }
  }

  return clonedIds;
}
